import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Button,
  Image,
  Stack,
  Badge,
  Icon,
  useColorModeValue,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { FaCheckCircle, FaArrowRight } from 'react-icons/fa';

const Products: React.FC = () => {
  const bgGradient = useColorModeValue(
    'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(13,211,205,0.05) 100%)',
    'linear-gradient(180deg, rgba(26,32,44,0) 0%, rgba(13,211,205,0.1) 100%)'
  );

  return (
    <Box py={20} pt="160px" position="relative" overflow="hidden" bgGradient={bgGradient}>
      <Container maxW="1200px" position="relative" zIndex={1}>
        <Box textAlign="center" mb={16}>
          <Badge
            colorScheme="teal"
            mb={4}
            px={3}
            py={1}
            borderRadius="full"
            fontWeight="bold"
          >
            领先的AI产品与服务
          </Badge>
          <Heading
            as="h1"
            size={{ base: "2xl", md: "3xl" }}
            fontWeight="bold"
            mb={6}
            bgGradient="linear(to-r, brand.cyan, teal.400)"
            bgClip="text"
          >
            产品服务
          </Heading>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            color="gray.600"
            maxW="800px"
            mx="auto"
            mb={12}
          >
            我们提供专业的人工智能产品和服务，帮助企业提高效率、降低成本、创造价值
          </Text>
        </Box>

        {/* 主要产品展示 */}
        {products.map((product, index) => (
          <Box
            key={index}
            mb={20}
            p={{ base: 6, md: 0 }}
            borderRadius="xl"
            overflow="hidden"
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center">
              <Box order={index % 2 === 0 ? 1 : 2}>
                <Badge
                  colorScheme={product.colorScheme}
                  mb={4}
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontWeight="bold"
                >
                  {product.category}
                </Badge>
                <Heading
                  as="h2"
                  size="xl"
                  mb={4}
                  color={product.titleColor}
                >
                  {product.title}
                </Heading>
                <Text color="gray.600" mb={6} lineHeight="tall">
                  {product.description}
                </Text>

                <List spacing={3} mb={8}>
                  {product.features.map((feature, idx) => (
                    <ListItem key={idx} display="flex" alignItems="center">
                      <ListIcon as={FaCheckCircle} color={product.titleColor} />
                      <Text>{feature}</Text>
                    </ListItem>
                  ))}
                </List>

                <Stack direction={{ base: "column", sm: "row" }} spacing={4}>
                  <Button
                    rightIcon={<FaArrowRight />}
                    colorScheme={product.colorScheme}
                    size="lg"
                    px={8}
                    py={6}
                    _hover={{
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)"
                    }}
                  >
                    了解详情
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme={product.colorScheme}
                    size="lg"
                    px={8}
                    py={6}
                    _hover={{
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)"
                    }}
                  >
                    申请试用
                  </Button>
                </Stack>
              </Box>
              <Flex
                order={index % 2 === 0 ? 2 : 1}
                justifyContent="center"
                alignItems="center"
              >
                <Box
                  bg={product.bgColor}
                  p={8}
                  borderRadius="xl"
                  boxShadow="0 10px 30px rgba(0, 0, 0, 0.1)"
                  width="100%"
                  maxW="500px"
                  aspectRatio="4/3"
                  position="relative"
                  overflow="hidden"
                >
                  <Image
                    src={product.imageSrc}
                    alt={product.title}
                    width="100%"
                    height="100%"
                    objectFit="contain"
                  />
                </Box>
              </Flex>
            </SimpleGrid>
          </Box>
        ))}
      </Container>
    </Box>
  );
};

const products = [
  {
    title: 'YS-Assistant',
    category: '智能对话助手',
    description: '企业级智能对话助手，基于先进大语言模型，支持多轮对话、知识库接入、流程自动化等功能，为企业提供24/7智能客服支持。',
    features: [
      '支持企业知识库定制与私有化部署',
      '多渠道集成：网站、APP、微信等',
      '多语言支持与情感分析',
      '智能工单流转与业务系统对接',
      '数据分析与持续优化'
    ],
    imageSrc: 'https://images.unsplash.com/photo-1596524430615-b46475ddff6e?ixlib=rb-4.0.3&auto=format&fit=crop',
    bgColor: 'rgba(124, 58, 237, 0.05)',
    titleColor: 'brand.purple',
    colorScheme: 'purple'
  },
  {
    title: 'YS-Vision',
    category: '计算机视觉平台',
    description: '一站式计算机视觉解决方案，提供图像识别、视频分析、对象检测等功能，适用于质检、安防、零售等多个行业场景。',
    features: [
      '高精度目标检测与识别',
      '实时视频流分析',
      '自定义模型训练与部署',
      '全平台SDK支持',
      '边缘计算与云端协同'
    ],
    imageSrc: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3&auto=format&fit=crop',
    bgColor: 'rgba(59, 130, 246, 0.05)',
    titleColor: 'brand.blue',
    colorScheme: 'blue'
  },
  {
    title: 'YS-Insight',
    category: '数据智能分析',
    description: '企业级数据分析平台，整合机器学习算法，提供数据可视化、预测分析、异常检测等功能，帮助企业挖掘数据价值。',
    features: [
      '多源数据集成与处理',
      '可视化报表与仪表盘',
      '预测模型构建与部署',
      '异常检测与预警',
      '决策建议生成'
    ],
    imageSrc: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop',
    bgColor: 'rgba(13, 211, 205, 0.05)',
    titleColor: 'brand.cyan',
    colorScheme: 'teal'
  }
];

export default Products;